/**
 * Offline copy of the user's repo in the app's own file storage (iOS app only).
 *
 * Every file and note in the repo is mirrored into the app's Library/NoCloud folder (not purged
 * under storage pressure, included in device backups, not shown in the Files app). Files
 * downloaded from the Solo 2 go to the Documents folder, which the Files app shows under
 * "On My iPhone > Mala Racing". Offline, the repo list is served from the mirror. The website
 * never uses this: there is no app folder to mirror into.
 *
 * To the user the phone and the cloud are one repo, and nothing here is asked for: each sync
 * uploads every session in Documents the repo has never had, removes from the cloud what was
 * deleted on the phone (offline deletes wait in the index), and removes from the phone, the
 * Documents copy included, what was deleted elsewhere.
 */
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Network } from '@capacitor/network'
import { isNativeApp } from './native'
import { fromBase64, toBase64 } from '../solo2/bridge'
import {
  deleteSession,
  deleteSessionNamed,
  downloadSession,
  listSessions,
  loadMeta,
  loadNotes,
  mergeMeta,
  saveNotes,
  uploadSession,
  type Notes,
  type SessionInfo,
  type SessionInfos,
  type StoredSession,
} from './storage'

const DIR = Directory.Library
const ROOT = 'NoCloud/repo'
const INDEX = `${ROOT}/_index.json`
/** An earlier build kept the mirror under Documents; it is removed on the first sync. */
const LEGACY_ROOT = 'repo'
const SETTING_KEY = 'mala.offline.v1'

export interface CachedFile {
  name: string
  /** Repo key once uploaded; absent while pending */
  key?: string
  size: number
  /** ISO time of the repo object, for change detection */
  lastModified: string
  /** Downloaded from the logger, not yet in the repo */
  pending?: boolean
}

export interface OfflineIndex {
  files: Record<string, CachedFile>
  notes: Notes
  /** Per-file info (date, laps, best, track), mirrored from the repo and set for queued files */
  meta?: SessionInfos
  /** Notes edited offline that still need to be pushed */
  notesDirty?: boolean
  /** Names deleted on this phone whose repo copy the next sync still has to remove */
  deleted?: string[]
  syncedAt?: string
}

export interface SyncStatus {
  state: 'idle' | 'syncing' | 'offline' | 'error'
  syncedAt?: string
  fileCount: number
  bytes: number
  pending: number
  /** Deletes made on this phone that the repo has not seen yet */
  deletes: number
  error?: string
}

export const offlineAvailable = isNativeApp

export function offlineEnabled(): boolean {
  try {
    return localStorage.getItem(SETTING_KEY) === '1'
  } catch {
    return false
  }
}

export function setOfflineEnabled(on: boolean): void {
  try {
    localStorage.setItem(SETTING_KEY, on ? '1' : '0')
  } catch {
    /* storage may be unavailable */
  }
}

// ---------- files ----------

const EMPTY: OfflineIndex = { files: {}, notes: {} }

/** All index read-modify-write sequences go through this chain so a queue add cannot be lost
 *  under a running sync (both used to read the index, then write their own copy back). */
let chain: Promise<unknown> = Promise.resolve()
export function withIndex<T>(fn: () => Promise<T>): Promise<T> {
  const run = chain.then(fn, fn)
  chain = run.catch(() => undefined)
  return run
}

export async function readIndex(): Promise<OfflineIndex> {
  try {
    const r = await Filesystem.readFile({ path: INDEX, directory: DIR })
    const text = typeof r.data === 'string' ? new TextDecoder().decode(fromBase64(r.data)) : await r.data.text()
    const parsed = JSON.parse(text) as OfflineIndex
    return {
      files: parsed.files ?? {},
      notes: parsed.notes ?? {},
      meta: parsed.meta ?? {},
      notesDirty: parsed.notesDirty,
      deleted: parsed.deleted ?? [],
      syncedAt: parsed.syncedAt,
    }
  } catch {
    return { ...EMPTY, files: {}, notes: {} }
  }
}

async function writeIndex(index: OfflineIndex): Promise<void> {
  await Filesystem.writeFile({
    path: INDEX,
    directory: DIR,
    data: toBase64(new TextEncoder().encode(JSON.stringify(index))),
    recursive: true,
  })
}

export async function saveCachedFile(name: string, bytes: ArrayBuffer, meta: Omit<CachedFile, 'name' | 'size'>, info?: SessionInfo): Promise<void> {
  await Filesystem.writeFile({ path: `${ROOT}/${name}`, directory: DIR, data: toBase64(new Uint8Array(bytes)), recursive: true })
  await withIndex(async () => {
    const index = await readIndex()
    index.files[name] = { name, size: bytes.byteLength, ...meta }
    if (info) index.meta = { ...(index.meta ?? {}), [name]: info }
    await writeIndex(index)
  })
}

/** Keep a file's info (date, laps, best lap, track) for a session that lives only on this phone. */
export async function rememberInfo(name: string, info: SessionInfo): Promise<void> {
  await withIndex(async () => {
    const index = await readIndex()
    index.meta = { ...(index.meta ?? {}), [name]: info }
    await writeIndex(index)
  })
}

export async function readCachedFile(name: string): Promise<ArrayBuffer | null> {
  try {
    const r = await Filesystem.readFile({ path: `${ROOT}/${name}`, directory: DIR })
    if (typeof r.data === 'string') {
      const u = fromBase64(r.data)
      return u.buffer.slice(u.byteOffset, u.byteOffset + u.byteLength) as ArrayBuffer
    }
    return await r.data.arrayBuffer()
  } catch {
    return null
  }
}

/**
 * Delete a session from the repo. Every copy on this phone goes now (the Files-app copy, the
 * mirror copy, its place in the upload queue); the repo's own copy goes with the next sync,
 * which is told by the name left in `deleted`. That works offline too. `remote` is false when
 * nobody is signed in: there is no repo to tell.
 */
export async function deleteFromRepo(name: string, remote: boolean): Promise<void> {
  await withIndex(async () => {
    await Filesystem.deleteFile({ path: `${ROOT}/${name}`, directory: DIR }).catch(() => undefined)
    await deleteDocument(name)
    const index = await readIndex()
    delete index.files[name]
    if (remote && !index.deleted?.includes(name)) index.deleted = [...(index.deleted ?? []), name]
    await writeIndex(index)
  })
}

/** Remove the whole mirror (account deletion, sign-out). */
export async function clearOffline(): Promise<void> {
  await Filesystem.rmdir({ path: ROOT, directory: DIR, recursive: true }).catch(() => undefined)
  await Filesystem.rmdir({ path: LEGACY_ROOT, directory: Directory.Data, recursive: true }).catch(() => undefined)
}

/** A copy the user can see and share: Files app, "On My iPhone > Mala Racing". */
export async function exportToFiles(name: string, bytes: ArrayBuffer): Promise<void> {
  await Filesystem.writeFile({ path: name, directory: Directory.Documents, data: toBase64(new Uint8Array(bytes)), recursive: true })
}

/** Queue a logger download for the repo: mirrored now, uploaded by the next sync. */
export async function queueForUpload(name: string, bytes: ArrayBuffer, info?: SessionInfo): Promise<void> {
  await saveCachedFile(name, bytes, { pending: true, lastModified: new Date().toISOString() }, info)
}

// ---------- The Files-app folder (Documents): sessions the user can see and share ----------

export interface DocumentFile {
  name: string
  size: number
  mtime: number
}

export async function listDocuments(): Promise<DocumentFile[]> {
  try {
    const { files } = await Filesystem.readdir({ directory: Directory.Documents, path: '' })
    return files
      .filter((f) => f.type === 'file' && /\.(xrk|xrz|hrz)$/i.test(f.name))
      .map((f) => ({ name: f.name, size: f.size, mtime: f.mtime }))
      .sort((a, b) => b.mtime - a.mtime)
  } catch {
    return []
  }
}

export async function readDocument(name: string): Promise<ArrayBuffer | null> {
  try {
    const r = await Filesystem.readFile({ path: name, directory: Directory.Documents })
    if (typeof r.data === 'string') {
      const u = fromBase64(r.data)
      return u.buffer.slice(u.byteOffset, u.byteOffset + u.byteLength) as ArrayBuffer
    }
    return await r.data.arrayBuffer()
  } catch {
    return null
  }
}

export async function deleteDocument(name: string): Promise<void> {
  await Filesystem.deleteFile({ path: name, directory: Directory.Documents }).catch(() => undefined)
}

/** Record a note edit; pushed by the next sync. */
export async function saveNoteOffline(notes: Notes): Promise<void> {
  await withIndex(async () => {
    const index = await readIndex()
    index.notes = notes
    index.notesDirty = true
    await writeIndex(index)
  })
}

/** The repo list as the mirror knows it, for use when the network is gone. */
export function cachedAsStored(index: OfflineIndex): StoredSession[] {
  return Object.values(index.files)
    .filter((f) => f.key)
    .map((f) => ({ key: f.key!, name: f.name, size: f.size, lastModified: new Date(f.lastModified) }))
    .sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime())
}

export function statusOf(index: OfflineIndex, state: SyncStatus['state'] = 'idle', error?: string): SyncStatus {
  const files = Object.values(index.files)
  return {
    state,
    syncedAt: index.syncedAt,
    fileCount: files.length,
    bytes: files.reduce((a, f) => a + f.size, 0),
    pending: files.filter((f) => f.pending).length,
    deletes: index.deleted?.length ?? 0,
    error,
  }
}

// ---------- sync ----------

export interface SyncPlan {
  download: StoredSession[]
  upload: CachedFile[]
  remove: string[]
}

/** What a sync has to do, given the mirror and the live repo list. Pure, for tests. */
export function planSync(index: OfflineIndex, cloud: StoredSession[]): SyncPlan {
  const byName = new Map(cloud.map((c) => [c.name, c]))
  const download = cloud.filter((c) => {
    const f = index.files[c.name]
    return !f || f.pending || f.size !== c.size || f.lastModified !== c.lastModified.toISOString()
  })
  const upload = Object.values(index.files).filter((f) => f.pending && !byName.has(f.name))
  const remove = Object.values(index.files)
    .filter((f) => !f.pending && !byName.has(f.name))
    .map((f) => f.name)
  return { download, upload, remove }
}

/**
 * Sessions in the Files-app folder that the repo has never had: they are queued for upload, so
 * every session on the phone reaches the repo without being asked for. Pure, for tests.
 */
export function docsToQueue(index: OfflineIndex, cloud: StoredSession[], docNames: string[]): string[] {
  const inRepo = new Set(cloud.map((c) => c.name))
  return docNames.filter((n) => !index.files[n] && !inRepo.has(n) && !index.deleted?.includes(n))
}

export async function isOnline(): Promise<boolean> {
  try {
    const s = await Network.getStatus()
    return s.connected && s.connectionType !== 'none'
  } catch {
    return typeof navigator === 'undefined' ? true : navigator.onLine
  }
}

let syncing: Promise<SyncStatus> | null = null

/**
 * Mirror the repo: push pending files and notes, pull new or changed files, drop files deleted
 * elsewhere. Resolves with the status; never throws. Concurrent calls share one run.
 */
export function syncRepo(onProgress?: (s: SyncStatus) => void): Promise<SyncStatus> {
  if (syncing) return syncing
  syncing = (async () => {
    // Files queued while a pass runs are picked up by another pass.
    let status = await syncPass(onProgress)
    for (let i = 0; i < 5 && status.state === 'idle' && status.pending + status.deletes > 0; i++) status = await syncPass(onProgress)
    return status
  })().finally(() => {
    syncing = null
  })
  return syncing
}

async function syncPass(onProgress?: (s: SyncStatus) => void): Promise<SyncStatus> {
  {
    await Filesystem.rmdir({ path: LEGACY_ROOT, directory: Directory.Data, recursive: true }).catch(() => undefined)
    let index = await readIndex()
    onProgress?.(statusOf(index, 'syncing'))
    let cloud: StoredSession[]
    let notes: Notes
    // When the repo list was read: a file that reached the mirror after that is missing from the
    // list because the list is old, not because it was deleted elsewhere.
    let listedAt = Date.now()
    try {
      ;[cloud, notes] = await Promise.all([listSessions(), loadNotes()])
    } catch (e) {
      return statusOf(index, 'offline', e instanceof Error ? e.message : String(e))
    }
    try {
      // Notes: offline edits win over the repo copy, then the merged set is pushed.
      if (index.notesDirty) {
        notes = { ...notes, ...index.notes }
        await saveNotes(notes)
        index.notesDirty = false
      }
      index.notes = notes

      // Deletes made on this phone go first, so nothing below brings those files back.
      const gone = index.deleted ?? []
      if (gone.length) {
        for (const name of gone) await deleteSessionNamed(name)
        cloud = cloud.filter((c) => !gone.includes(c.name))
        index = await withIndex(async () => {
          const latest = await readIndex()
          latest.deleted = (latest.deleted ?? []).filter((n) => !gone.includes(n))
          await writeIndex(latest)
          return latest
        })
      }

      // Sessions on the phone that the repo has never had join the upload queue.
      const docNames = (await listDocuments()).map((d) => d.name)
      for (const name of docsToQueue(index, cloud, docNames)) {
        const bytes = await readDocument(name)
        if (!bytes) continue
        index = await withIndex(async () => {
          const latest = await readIndex()
          // Deleted or queued by someone else since the list was read: leave it be.
          if (docsToQueue(latest, cloud, [name]).length === 0) return latest
          await Filesystem.writeFile({ path: `${ROOT}/${name}`, directory: DIR, data: toBase64(new Uint8Array(bytes)), recursive: true })
          latest.files[name] = { name, size: bytes.byteLength, pending: true, lastModified: new Date().toISOString() }
          await writeIndex(latest)
          return latest
        })
        onProgress?.(statusOf(index, 'syncing'))
      }

      const plan = planSync(index, cloud)
      const uploadedInfo: SessionInfos = {}
      for (const f of plan.upload) {
        const bytes = await readCachedFile(f.name)
        if (!bytes) {
          delete index.files[f.name]
          continue
        }
        const key = await uploadSession(new File([bytes], f.name))
        // Re-read under the lock: another file may have been queued meanwhile, or this one
        // deleted while it was uploading.
        const after = await withIndex(async () => {
          const latest = await readIndex()
          const entry = latest.files[f.name]
          if (!entry) return { latest, kept: false }
          latest.files[f.name] = { ...entry, key, pending: false, lastModified: new Date().toISOString() }
          await writeIndex(latest)
          return { latest, kept: true }
        })
        index = after.latest
        if (!after.kept) {
          // Deleted meanwhile: the upload must not bring it back.
          await deleteSession(key).catch(() => undefined)
          continue
        }
        if (index.meta?.[f.name]) uploadedInfo[f.name] = index.meta[f.name]
        onProgress?.(statusOf(index, 'syncing'))
      }
      if (plan.upload.length) {
        listedAt = Date.now()
        cloud = await listSessions()
      }
      // Per-file info: push what the queue knew, then mirror the repo's copy. Info for files
      // that are only on this phone stays, the repo's wins for files it knows.
      const repoMeta = Object.keys(uploadedInfo).length ? await mergeMeta(uploadedInfo) : await loadMeta()
      index.meta = { ...(index.meta ?? {}), ...repoMeta }

      for (const c of planSync(index, cloud).download) {
        const bytes = await downloadSession(c.key)
        index = await withIndex(async () => {
          const latest = await readIndex()
          // Deleted on this phone while it was downloading: the next pass removes the repo copy.
          if (latest.deleted?.includes(c.name)) return latest
          await Filesystem.writeFile({ path: `${ROOT}/${c.name}`, directory: DIR, data: toBase64(new Uint8Array(bytes)), recursive: true })
          latest.files[c.name] = { name: c.name, key: c.key, size: c.size, lastModified: c.lastModified.toISOString() }
          await writeIndex(latest)
          return latest
        })
        onProgress?.(statusOf(index, 'syncing'))
      }
      const meta = index.meta
      index = await withIndex(async () => {
        const latest = await readIndex()
        // Deleted from the repo elsewhere: gone here too, the Files-app copy included (left
        // behind, it would count as a new session and be uploaded again).
        for (const name of planSync(latest, cloud).remove) {
          if (Date.parse(latest.files[name].lastModified) >= listedAt) continue
          await Filesystem.deleteFile({ path: `${ROOT}/${name}`, directory: DIR }).catch(() => undefined)
          await deleteDocument(name)
          delete latest.files[name]
        }
        // Keys can change when a file is re-uploaded; keep them current.
        for (const c of cloud) if (latest.files[c.name]) latest.files[c.name].key = c.key
        latest.meta = meta
        latest.syncedAt = new Date().toISOString()
        await writeIndex(latest)
        return latest
      })
      return statusOf(index, 'idle')
    } catch (e) {
      index = await readIndex()
      return statusOf(index, 'error', e instanceof Error ? e.message : String(e))
    }
  }
}
